/**
 * Partner API tearsheet export contracts (9AT Connect → HedgeFacts).
 *
 * Request shape matches docs/tearsheets/9at/hedgefacts-export-sample.json.
 * POST returns an HF reportId; GET polls PDF status (reuses TearsheetReportPdf).
 */
import { Type, type Static } from '@sinclair/typebox';
import { TearsheetPdfPageMode, TearsheetReportPdf } from '../../web/interfaces/tearsheets';
import { StringEnum, createSchema } from '../../util/schema';

export const PartnerMonthlyReturnPoint = Type.Object(
  {
    year: Type.Integer(),
    month: Type.Integer({ minimum: 1, maximum: 12 }),
    /** Whole percent (2.00 = 2%). Null when the month has no reported return. */
    returnPercent: Type.Union([Type.Number(), Type.Null()]),
  },
  { $id: 'PartnerMonthlyReturnPoint' },
);
export type PartnerMonthlyReturnPoint = Static<typeof PartnerMonthlyReturnPoint>;

export const PartnerTearsheetContact = Type.Object(
  {
    name: Type.String(),
    title: Type.Optional(Type.String()),
    email: Type.Optional(Type.String({ format: 'email' })),
    phone: Type.Optional(Type.String()),
  },
  { $id: 'PartnerTearsheetContact' },
);
export type PartnerTearsheetContact = Static<typeof PartnerTearsheetContact>;

export const PartnerTearsheetSubscription = Type.Object(
  {
    minimumInvestment: Type.Optional(Type.Number()),
    managementFeePercent: Type.Optional(Type.Number()),
    performanceFeePercent: Type.Optional(Type.Number()),
  },
  { $id: 'PartnerTearsheetSubscription' },
);
export type PartnerTearsheetSubscription = Static<typeof PartnerTearsheetSubscription>;

export const PartnerStdDevMethod = StringEnum(['Geometric', 'StandardDeviation'], {
  $id: 'PartnerStdDevMethod',
  description: 'Which volatility methodology the unsuffixed risk fields mirror.',
});
export type PartnerStdDevMethod = Static<typeof PartnerStdDevMethod>;

export const PartnerTearsheetRisk = Type.Object(
  {
    sharpeRatio: Type.Optional(Type.Number()),
    sortinoRatio: Type.Optional(Type.Number()),
    maxDrawdownPercent: Type.Optional(Type.Number()),
    annualizedReturn: Type.Optional(Type.Number()),
    annualizedStdDev: Type.Optional(Type.Number()),
    downsideDeviation: Type.Optional(Type.Number()),
    correlation: Type.Optional(Type.Number()),
    stdDevMethod: Type.Optional(PartnerStdDevMethod),
    sharpeRatioStandard: Type.Optional(Type.Number()),
    sharpeRatioGeometric: Type.Optional(Type.Number()),
    sortinoRatioStandard: Type.Optional(Type.Number()),
    sortinoRatioGeometric: Type.Optional(Type.Number()),
    annualizedStdDevStandard: Type.Optional(Type.Number()),
    annualizedStdDevGeometric: Type.Optional(Type.Number()),
    downsideDeviationStandard: Type.Optional(Type.Number()),
    downsideDeviationGeometric: Type.Optional(Type.Number()),
    monthlyStdDevStandard: Type.Optional(Type.Number()),
    monthlyStdDevGeometric: Type.Optional(Type.Number()),
  },
  { $id: 'PartnerTearsheetRisk' },
);
export type PartnerTearsheetRisk = Static<typeof PartnerTearsheetRisk>;

export const PartnerTearsheetBenchmark = Type.Object(
  {
    id: Type.Integer(),
    symbol: Type.String({ minLength: 1 }),
    name: Type.String({ minLength: 1 }),
    isCustom: Type.Boolean(),
    monthlyReturns: Type.Array(PartnerMonthlyReturnPoint),
  },
  { $id: 'PartnerTearsheetBenchmark' },
);
export type PartnerTearsheetBenchmark = Static<typeof PartnerTearsheetBenchmark>;

/** 9AT Connect fact-sheet export body (POST /tearsheets). */
export const PartnerTearsheetExportRequest = Type.Object(
  {
    reportId: Type.Integer({ description: '9AT primary key of the fact sheet record.' }),
    companyId: Type.Integer(),
    companyName: Type.String({ minLength: 1 }),
    fundId: Type.Integer(),
    fundName: Type.String({ minLength: 1 }),
    programName: Type.Optional(Type.String()),
    programDescription: Type.Optional(Type.String()),
    about: Type.Optional(Type.String()),
    logoUrl: Type.Optional(Type.String()),
    contact: Type.Optional(PartnerTearsheetContact),
    subscription: Type.Optional(PartnerTearsheetSubscription),
    risk: Type.Optional(PartnerTearsheetRisk),
    monthlyReturns: Type.Array(PartnerMonthlyReturnPoint, { minItems: 1 }),
    benchmarks: Type.Optional(Type.Array(PartnerTearsheetBenchmark)),
  },
  { $id: 'PartnerTearsheetExportRequest' },
);
export type PartnerTearsheetExportRequest = Static<typeof PartnerTearsheetExportRequest>;
export const partnerTearsheetExportRequestSchema = createSchema(PartnerTearsheetExportRequest);

/** HF report id returned after accepting an export. */
export const PartnerTearsheetCreateResponse = Type.Object(
  {
    reportId: Type.String({ minLength: 1 }),
  },
  { $id: 'PartnerTearsheetCreateResponse' },
);
export type PartnerTearsheetCreateResponse = Static<typeof PartnerTearsheetCreateResponse>;
export const partnerTearsheetCreateResponseSchema = createSchema(PartnerTearsheetCreateResponse);

export const PartnerTearsheetGetQuery = Type.Object(
  {
    reportId: Type.String({ minLength: 1 }),
    pdfPageMode: Type.Optional(TearsheetPdfPageMode),
  },
  { $id: 'PartnerTearsheetGetQuery' },
);
export type PartnerTearsheetGetQuery = Static<typeof PartnerTearsheetGetQuery>;
export const partnerTearsheetGetQuerySchema = createSchema(PartnerTearsheetGetQuery);

/** PDF poll response — same shape as web TearsheetReportPdf. */
export const PartnerTearsheetPdfResponse = TearsheetReportPdf;
export type PartnerTearsheetPdfResponse = TearsheetReportPdf;
export const partnerTearsheetPdfResponseSchema = createSchema(PartnerTearsheetPdfResponse);
